import { useRef } from 'react';
import { useMapLifecycle } from '@/hooks/use-map-lifecycle';
import { useMapSubscriptions } from '@/hooks/use-map-subscriptions';
import { useMapClick } from '@/hooks/use-map-click';
import { useDrawingInteraction } from '@/hooks/use-drawing-interaction';
import { useShapeSubscriptions } from '@/hooks/use-shape-subscriptions';
import { useH3Subscriptions } from '@/hooks/use-h3-subscriptions';
import { useTrackLayers } from '@/hooks/use-track-layers';
import { useTrajectoryLayers } from '@/hooks/use-trajectory-layers';
import { useViewportCulling } from '@/hooks/use-viewport-culling';
import { useFollowEntity } from '@/hooks/use-follow-entity';
import { useExternalFlyTo } from '@/hooks/use-external-flyto';
import { useCoverageFootprint } from '@/hooks/use-coverage-footprint';
import { useStreetView } from '@/hooks/use-street-view';
import { useHistoryBuffer } from '@/hooks/use-history-buffer';
import { useHistoryPlayback } from '@/hooks/use-history-playback';
import { ICON_RAIL_WIDTH } from '@/constants/layout';
import { LayerSwitcher } from './layer-switcher';
import { TrackLayerSwitcher } from './track-layer-switcher';
import { TrajectoryControl } from './trajectory-control';
import { AiChatBubble } from './ai-chat-bubble';
import { H3Controls } from './h3-controls';
import { SearchBar } from './search-bar';
import { UnitSearch } from './unit-search';
import { SelectionCard } from './selection-card';
import { StreetViewPanel } from './street-view-panel';
import { ResetViewControl } from './reset-view-control';
import { LaunchDashboardControl } from './launch-dashboard-control';
import { MapLabelsControl } from './map-labels-control';
import { EntityDetailModal } from './entity-detail-modal';
import { TimelineBar } from './timeline-bar';
import { LaunchDashboard } from '@/components/launch/launch-dashboard';

/** Full-screen map container offset by the icon rail width */
export function MapContainer(): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mapRef, modeRef } = useMapLifecycle(containerRef);

  useMapSubscriptions(mapRef, modeRef);
  useMapClick(mapRef, modeRef);
  useDrawingInteraction(mapRef, modeRef);
  useShapeSubscriptions(mapRef, modeRef);
  useH3Subscriptions(mapRef, modeRef);
  useTrackLayers(mapRef, modeRef);
  useTrajectoryLayers(mapRef, modeRef);
  useViewportCulling(mapRef, modeRef);
  useFollowEntity(mapRef, modeRef);
  useExternalFlyTo();
  useCoverageFootprint(mapRef, modeRef);
  useStreetView(mapRef, modeRef);
  useHistoryBuffer();
  useHistoryPlayback(mapRef, modeRef);

  return (
    <>
      <div
        ref={containerRef}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: ICON_RAIL_WIDTH, zIndex: 0 }}
      />
      <LayerSwitcher />
      <SearchBar />
      <SelectionCard />
      <EntityDetailModal />
      <StreetViewPanel />
      <TimelineBar />
      <LaunchDashboard />
      <AiChatBubble />
      <div className="absolute right-3 top-3 z-[1000] flex flex-col items-end gap-2">
        <H3Controls />
        <TrackLayerSwitcher />
        <TrajectoryControl />
        <UnitSearch />
        <LaunchDashboardControl />
        <MapLabelsControl />
        <ResetViewControl />
      </div>
    </>
  );
}
